import React from 'react';

import { Paper, Stack } from '@mui/material';

import { isRowApproved, isRowManuallyApproved } from '../../manualOverridePersistence';
import ChecklistRuleCard from '../../reportCards/ChecklistRuleCard';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReportCard from '../../reportCards/ReportCard';
import type { ManualOverrideState } from '../../types';

import type { DiscountsMarginChecklistRow } from './types';

interface Props {
  expandedRows: Record<string, boolean>;
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<DiscountsMarginChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onToggleDetails: (rowId: string, defaultOpen?: boolean) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function DiscountsMarginReportCard({
  expandedRows,
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onToggleDetails,
  onUnapprovalChange,
}: Props) {
  return (
    <ReportCard bordered={false}>
      <Stack spacing={1.5} sx={{ pb: 2 }}>
        {rows.length === 0 ? (
          <Paper variant="outlined" sx={{ color: 'text.secondary', p: 4, textAlign: 'center' }}>
            No Discounts &amp; Margin rows match this view.
          </Paper>
        ) : (
          rows.map((row) => {
            const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
            const approved = isRowApproved(row, manualOverrides);
            const isExpanded = expandedRows[row.id] ?? true;

            return (
              <ChecklistRuleCard
                key={row.id}
                approved={approved}
                dataPoints={row.details.map((detail) => ({
                  expandableItems: detail.expandableItems,
                  label: detail.label,
                  readOnly: true,
                  value: detail.value,
                }))}
                expanded={isExpanded}
                manuallyApproved={manuallyApproved}
                rule={row.rule}
                summary={row.summary}
                title={row.title}
                onApprovalChange={() => onApprovalChange(row.id)}
                onUnapprovalChange={() => onUnapprovalChange(row.id)}
                onToggleExpanded={() => onToggleDetails(row.id, true)}
              />
            );
          })
        )}
      </Stack>
      <HiddenApprovedFooter
        count={hiddenApprovedCount}
        noun="Discounts & Margin row"
        showAutoApproved={showAutoApproved}
      />
    </ReportCard>
  );
}
